<template>
  <div class="method-details-container">
    <!-- Full page background -->
    <div class="full-page-background">
      <img
        src="/img/homepage_bg.webp"
        alt="Background"
        class="background-image"
      />
    </div>

    <!-- Page content overlay -->
    <div class="page-content">
      <!-- Header -->
      <div class="page-header">
        <div v-if="activeMethod" class="method-hero">
          <div class="method-hero-icon-chip">
            <img
              :src="activeMethod.imageUrl"
              :alt="activeMethod.name"
              class="method-icon"
            />
          </div>
          <h1 class="page-title">{{ activeMethod.name }}</h1>
        </div>
      </div>

      <!-- Method content -->
      <div class="method-content" v-if="activeMethod">
        <!-- What is it? / How does it work? / How well does it work? -->
        <div class="info-grid">
          <div class="info-card" :style="{ borderTopColor: activeMethodColor }">
            <p class="info-card-label" :style="{ color: activeMethodColor }">
              What is it?
            </p>
            <div class="info-card-body" v-html="activeMethod.whatIsIt"></div>
          </div>
          <div class="info-card" :style="{ borderTopColor: activeMethodColor }">
            <p class="info-card-label" :style="{ color: activeMethodColor }">
              How does it work?
            </p>
            <div
              class="info-card-body"
              v-html="activeMethod.howDoesItWork"
            ></div>
          </div>
          <div class="info-card" :style="{ borderTopColor: activeMethodColor }">
            <p class="info-card-label" :style="{ color: activeMethodColor }">
              How well does it work?
            </p>
            <div
              class="info-card-body"
              v-html="activeMethod.howWellDoesItWork"
            ></div>
          </div>
        </div>

        <!-- How is it used? -->
        <div class="used-card" :style="{ background: activeMethodColor }">
          <p class="used-card-label">How is it used?</p>
          <div class="used-card-body" v-html="activeMethod.howIsItUsed"></div>
        </div>

        <!-- Side effects -->
        <div class="warning-card">
          <p class="warning-card-label">⚠️ Side effects</p>
          <div
            class="warning-card-body"
            v-html="activeMethod.whatAreTheSideEffect"
          ></div>
        </div>

        <!-- Considerations -->
        <div class="plain-card" v-if="sortedConsiderations.length > 0">
          <p class="plain-card-label" :style="{ color: activeMethodColor }">
            Things to consider
          </p>
          <div class="consideration-list">
            <div
              v-for="(item, index) in sortedConsiderations"
              :key="index"
              class="consideration-row"
              :style="{ borderLeftColor: activeMethodColor }"
            >
              <span
                class="consideration-arrow"
                :style="{ color: activeMethodColor }"
                >→</span
              >
              <span class="consideration-row-text" v-html="item.text"></span>
            </div>
          </div>
        </div>

        <!-- FAQs -->
        <div class="plain-card" v-if="sortedFaqs.length > 0">
          <p class="plain-card-label" :style="{ color: activeMethodColor }">
            Frequently asked questions
          </p>
          <div class="faq-accordion">
            <div
              v-for="(faq, index) in sortedFaqs"
              :key="index"
              class="faq-block"
            >
              <div
                class="faq-block-question"
                :style="
                  openFaqIndex === index
                    ? { background: activeMethodColor, color: '#ffffff' }
                    : {
                        background: hexToRgba(activeMethodColor, 0.1),
                        color: activeMethodColor,
                      }
                "
                @click="toggleFaq(index)"
              >
                <span>{{ faq.question }}</span>
                <v-icon
                  size="18"
                  :style="{
                    transform:
                      openFaqIndex === index
                        ? 'rotate(180deg)'
                        : 'rotate(0deg)',
                    transition: 'transform 0.3s ease',
                  }"
                  >mdi-chevron-down</v-icon
                >
              </div>
              <div
                v-if="openFaqIndex === index"
                class="faq-block-answer"
                v-html="faq.answer"
              ></div>
            </div>
          </div>
        </div>

        <!-- Disclaimer -->
        <div class="details-disclaimer">
          <p>
            ⚠️ This information does not replace medical advice. Please speak to
            a healthcare provider before starting any HIV prevention method.
          </p>
        </div>
      </div>
    </div>

    <!-- Fixed bottom buttons -->
    <div class="fixed-bottom-buttons">
      <v-app-bar location="bottom" class="bottom-bar">
        <div class="results-footer-row">
          <v-btn
            class="back-button"
            size="large"
            @click="$router.go(-1)"
            variant="elevated"
          >
            BACK
          </v-btn>
          <v-btn
            class="action-btn find-clinic-btn"
            size="large"
            @click="navigateToServiceFinder"
            variant="elevated"
          >
            FIND A CLINIC
          </v-btn>
          <v-btn
            class="action-btn chat-btn"
            size="large"
            @click="navigateToWhatsapp"
            variant="elevated"
          >
            <v-icon left>mdi-whatsapp</v-icon>
            CHAT
          </v-btn>
        </div>
      </v-app-bar>
    </div>
  </div>
</template>

<script>
import MethodDataService from "../services/MethodDataService";
export default {
  name: "MethodDetails",
  props: ["choice"],
  data: () => ({
    activeMethod: null,
    openFaqIndex: null,
  }),
  async created() {
    this.activeMethod = await MethodDataService.get(this.choice);
    console.log(this.activeMethod);
  },
  computed: {
    activeMethodColor() {
      return this.activeMethod && this.activeMethod.color
        ? this.activeMethod.color
        : "#000000";
    },
    sortedConsiderations() {
      if (!this.activeMethod || !this.activeMethod.considerations) return [];
      return [...this.activeMethod.considerations].sort(
        (a, b) => (Number(a.position) || 0) - (Number(b.position) || 0),
      );
    },
    sortedFaqs() {
      if (!this.activeMethod || !this.activeMethod.faqs) return [];
      return [...this.activeMethod.faqs].sort(
        (a, b) => (Number(a.position) || 0) - (Number(b.position) || 0),
      );
    },
  },
  methods: {
    toggleFaq(index) {
      this.openFaqIndex = this.openFaqIndex === index ? null : index;
    },
    navigateToServiceFinder() {
      this.$gtag.event("Navigation to Service Finder", {
        event_category: "navigation_to_service_finder",
        event_label: "navigation_to_service_finder_from_method_details_page",
        value: "method_details_page",
      });
      window.open(
        "https://witsrhi-servicefinder-client.onrender.com/",
        "_blank",
      );
    },
    navigateToWhatsapp() {
      this.$gtag.event("Navigation to Talk to Somebody", {
        event_category: "navigation_to_talk_to_somebody",
        event_label: "navigation_to_talk_to_somebody_from_method_details_page",
        value: "method_details_page",
      });
      window.open("https://api.whatsapp.com/send?phone=27781680192", "_blank");
    },
    hexToRgba(hex, alpha) {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    },
  },
};
</script>

<style lang="scss" scoped>
// Method Details Page with full-page background (matching QuizResultsPage)

.method-details-container {
  height: 100vh;
  position: relative;
  overflow-y: scroll; // Always reserve the scrollbar gutter so content width stays stable
  overflow-x: hidden;
  background: transparent;
  scrollbar-width: thin; // Firefox — force a persistent, non-overlay scrollbar
  scrollbar-color: rgba(255, 255, 255, 0.7) rgba(255, 255, 255, 0.15);

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.15);
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.7);
    border-radius: 4px;
  }
}

.full-page-background {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 0;
}

.background-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 30%;
}

.page-content {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  padding: 0;
  padding-bottom: 120px; // Space for fixed bottom button
}

.page-header {
  padding: 20px 24px;
  flex-shrink: 0;
  max-width: 960px;
  margin: 0 auto;
  box-sizing: border-box;

  @media (max-height: 480px) and (orientation: landscape) {
    padding: 10px 24px;
  }
}

.method-hero {
  text-align: center;
  padding: 8px 0;
}

.method-hero-icon-chip {
  width: 90px;
  height: 90px;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.22);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 2px solid rgba(255, 255, 255, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 14px;
}

.method-icon {
  width: 56px;
  height: 56px;
  object-fit: contain;
}

.page-title {
  color: white;
  font-size: 28px;
  font-weight: 900;
  font-family: "Montserrat", sans-serif;
  text-align: center;
  margin: 0;
  line-height: 1.15;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.method-content {
  padding: 0 24px 20px 24px;
  max-width: 960px;
  margin: 0 auto;
  box-sizing: border-box;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.fixed-bottom-buttons {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1000;
}

.bottom-bar {
  border: none !important;
}

.results-footer-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-around;
  gap: 8px;
  width: 100%;
}

.back-button {
  background: #eb63eb !important;
  color: white !important;
  border-radius: 10px !important;
  font-weight: bold !important;
  font-size: 12px !important;
  text-transform: none !important;
  padding: 0 16px !important;
  height: 50px !important;
  min-width: 100px !important;
  box-shadow: 0 4px 12px rgba(235, 99, 235, 0.3) !important;

  @media (max-width: 360px) {
    min-width: 84px !important;
    padding: 0 10px !important;
  }
}

.action-btn {
  font-weight: 700 !important;
  font-size: 12px !important;
  text-transform: none !important;
  border-radius: 10px !important;
  height: 50px !important;
  min-width: 100px !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2) !important;

  @media (max-width: 360px) {
    min-width: 84px !important;
    padding: 0 10px !important;
  }
}

.find-clinic-btn {
  background: #e38fe5 !important;
  color: white !important;
}

.chat-btn {
  background: #05bf99 !important;
  color: white !important;
}

// Flat, always-visible info cards (matching the prep-quiz-kit reference)
%rich-text {
  :deep(strong) {
    color: inherit;
    font-weight: 700;
  }

  :deep(p) {
    margin: 0 0 12px;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

.info-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  margin-bottom: 16px;

  @media (min-width: 640px) {
    grid-template-columns: repeat(3, 1fr);
  }
}

.info-card {
  @extend %rich-text;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 16px;
  border-top: 4px solid #3d80e8;
  padding: 18px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.info-card-label {
  font-weight: 700;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin: 0 0 10px;
}

.info-card-body {
  color: #333;
  font-size: 0.92rem;
  line-height: 1.6;
}

.used-card {
  border-radius: 16px;
  padding: 18px;
  margin-bottom: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.used-card-label {
  color: rgba(255, 255, 255, 0.8);
  font-weight: 700;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin: 0 0 10px;
}

.used-card-body {
  @extend %rich-text;
  color: #ffffff;
  font-size: 0.92rem;
  line-height: 1.7;
}

.warning-card {
  background: #fff5f0;
  border: 2px solid #ffcbb3;
  border-radius: 16px;
  padding: 18px;
  margin-bottom: 16px;
}

.warning-card-label {
  color: #e05a00;
  font-weight: 700;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin: 0 0 10px;
}

.warning-card-body {
  @extend %rich-text;
  color: #555;
  font-size: 0.92rem;
  line-height: 1.6;
}

.plain-card {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 16px;
  padding: 18px;
  margin-bottom: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.plain-card-label {
  font-weight: 700;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin: 0 0 14px;
}

.consideration-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.consideration-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 14px;
  background: rgba(61, 128, 232, 0.05);
  border-radius: 10px;
  border-left: 3px solid #3d80e8;
}

.consideration-arrow {
  font-weight: 700;
  flex-shrink: 0;
}

.consideration-row-text {
  color: #333;
  font-size: 0.92rem;
  line-height: 1.5;
}

.faq-accordion {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.faq-block-question {
  border-radius: 10px;
  padding: 14px 18px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  font-weight: 700;
  font-size: 0.92rem;
  transition: all 0.2s ease;
}

.faq-block-answer {
  @extend %rich-text;
  background: rgba(61, 128, 232, 0.05);
  border: 1px solid rgba(61, 128, 232, 0.15);
  border-radius: 10px;
  margin-top: 6px;
  padding: 14px 18px;
  color: #333;
  font-size: 0.9rem;
  line-height: 1.6;
}

.details-disclaimer {
  background: rgba(255, 255, 255, 0.6);
  border-radius: 14px;
  padding: 16px 20px;

  p {
    margin: 0;
    color: #555;
    font-size: 14px;
    line-height: 1.6;
    text-align: center;
    font-family: "Montserrat", sans-serif;
  }
}

// Override global styles for this page — transparent bottom bar, matching HomePage
:deep(.v-app-bar) {
  background: transparent !important;
  box-shadow: none !important;
}
</style>
